import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserProfileMenu from './UserProfileMenu';
import { 
  LayoutDashboard, 
  Target, 
  Calendar, 
  FileText, 
  Users, 
  Sparkles, 
  Settings, 
  LogOut,
  ChevronDown,
  BarChart3,
  FileBarChart
} from 'lucide-react';
import { Button } from './ui/button';

const Sidebar = ({ onNavigate }) => {
  const { user, logout, isOwner, isExecutive } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  // Miller's Law: Group navigation items into logical chunks (max 4-5 per group)
  const mainNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/daily-ops', icon: Calendar, label: 'Daily Ops' },
  ];

  const workspaceItems = [
    { to: '/notes', icon: FileText, label: 'Notas' },
    { to: '/ai-drafts', icon: Sparkles, label: 'Tareas IA' },
  ];

  // Analytics group for executives and owners
  const analyticsItems = (isExecutive || isOwner) ? [
    { to: '/trends', icon: BarChart3, label: 'Tendencias' },
    { to: '/reports', icon: FileBarChart, label: 'Reportes' }
  ] : [];

  // Admin group for owners only
  const adminItems = isOwner ? [
    { to: '/delegation', icon: Users, label: 'Delegación' },
    { to: '/admin', icon: Settings, label: 'Admin' }
  ] : [];

  const NavGroup = ({ label, items }) => (
    items.length > 0 && (
      <div className="nav-group">
        {label && <div className="nav-group-label">{label}</div>}
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={handleNavClick}
            className={({ isActive }) =>
              isActive ? 'sidebar-item-active' : 'sidebar-item'
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-4 h-4" strokeWidth={1.5} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </div>
    )
  );

  return (
    <aside className="w-60 h-screen bg-notion-bg-gray border-r border-gray-200 flex flex-col">
      {/* Logo - Fitts's Law: Large clickable area */}
      <NavLink to="/dashboard" className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gray-900 rounded-md flex items-center justify-center">
            <span className="text-white text-xs font-bold">U</span>
          </div>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">UrpeOS</h1>
            <p className="text-[10px] text-gray-500">Q1 2026</p>
          </div>
        </div>
      </NavLink>

      {/* Navigation - Miller's Law: Grouped into chunks */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {/* Main Navigation - Always visible, most used */}
        <NavGroup items={mainNavItems} />
        
        {/* Workspace - Daily work items */}
        <NavGroup label="Workspace" items={workspaceItems} />
        
        {/* Analytics - For data analysis */}
        <NavGroup label="Analytics" items={analyticsItems} />
        
        {/* Admin - Administrative functions */}
        <NavGroup label="Administrar" items={adminItems} />
      </nav>

      {/* User Section - Fitts's Law: Easy to reach at bottom */}
      <div className="p-3 border-t border-gray-200">
        <UserProfileMenu />
      </div>
    </aside>
  );
};

export default Sidebar;
